/*!
* # SeriouslyRAD
* http://www.seriouslyrad.com/
* 
* Copyright (c) 2016 - 2024 Seriously RAD Limited - All Rights Reserved
* Unauthorized copying of this file, via any medium is strictly prohibited
* Proprietary and confidential
*
*/

(() =>
{
    "use strict";

    const
        htmlEventNames =
            Object.keys(window)
                .filter(item => /\bon/.test(item))
                .map(item => item.substring(2))
                .reduce((a, b) => ({ ...a, [b]: true }), {}),
        numericToPixelProperties = [
            "background-position",
            "border",
            "border-bottom",
            "border-bottom-width",
            "border-left",
            "border-left-width",
            "border-right",
            "border-right-width",
            "border-spacing",
            "border-top",
            "border-top-width",
            "border-width",
            "bottom",
            "font",
            "font-size",
            "height",
            "left",
            "letter-spacing",
            "line-height",
            "margin",
            "margin-bottom",
            "margin-left",
            "margin-right",
            "margin-top",
            "max-height",
            "max-width",
            "min-height",
            "min-width",
            "outline",
            "outline-width",
            "padding",
            "padding-bottom",
            "padding-left",
            "padding-right",
            "padding-top",
            "right",
            "text-indent",
            "top",
            "vertical-align",
            "width",
            "word-spacing"
        ],
        parseCssSize = s =>
        {
            const
                results = s.match(/(-?[\d.]+)([a-z%]*)/);

            return {
                value: parseFloat(results[1]),
                unit: results[2]
            }
        };

    class Animator
    {
        _obj;
        _objAnimate;
        _propertyName;
        _targetValue;
        _targetUnit;
        _duration;
        _easingFunction;

        _objElement;
        _stop = false;
        _finish = false;

        constructor(obj, propertyName, targetValue, duration, easingFunction)
        {
            this._obj = obj;
            this._propertyName = propertyName;

            if (this._obj instanceof Document && Object.hasProperty(this._obj.body.style, this._propertyName))
            {
                this._objAnimate = this._obj.body.style;
                this._objElement = this._obj.body;
            }
            else if (this._obj instanceof Document && Object.hasProperty(this._obj.body, this._propertyName))
            {
                this._objAnimate = this._obj.body;
                this._objElement = this._obj.body;
            }
            else if (this._obj instanceof Element && Object.hasProperty(this._obj.style, this._propertyName))
            {
                this._objAnimate = this._obj.style;
                this._objElement = this._obj;
            }
            else
                this._objAnimate = this._obj;

            const
                propertyNameDashed = this._propertyName.toDashed(),
                trimmedTargetValue = `${targetValue}`.trim(),
                size = parseCssSize(trimmedTargetValue);

            this._targetValue = size.value;
            this._targetUnit = size.unit;

            if (!this._targetUnit && numericToPixelProperties.indexOf(propertyNameDashed) >= 0)
                this._targetUnit = "px";

            this._duration = duration || 400;
            this._easingFunction = easingFunction || "easeInOutQuart";
        }

        start()
        {
            let
                startDate = +new Date(),
                originalValueString = this._objAnimate[this._propertyName];

            if (!originalValueString)
            {
                if (this._objAnimate instanceof CSSStyleDeclaration && this._objElement)
                {
                    const
                        computedStyle = getComputedStyle(this._objElement);

                    originalValueString = computedStyle[this._propertyName];
                }
            }

            if (!originalValueString)
                originalValueString = "0px";

            let
                originalValue = parseFloat(originalValueString),
                currentValue = originalValue;

            if (currentValue === this._targetValue)
                return Promise.resolve();

            let
                resolveOuter;

            const
                tick = () =>
                {
                    const
                        factor =
                            this._finish
                                ? 1
                                : $$.easing[this._easingFunction](Math.clamp((new Date() - startDate) / this._duration, 0, 1));

                    currentValue = originalValue + factor * (this._targetValue - originalValue);

                    this._objAnimate[this._propertyName] =
                        this._targetUnit
                            ? `${currentValue}${this._targetUnit}`
                            : currentValue;

                    if (factor === 1)
                    {
                        resolveOuter();
                        return;
                    }

                    if (this._stop)
                        resolveOuter("Animation stopped");
                    else
                        (window.requestAnimationFrame && requestAnimationFrame(tick)) || setTimeout(tick, 16);
                };

            return new Promise((resolve, reject) =>
            {
                resolveOuter = resolve;
                tick();
            });
        }
        stop()
        {
            this._stop = true;
        }
        finish()
        {
            this._finish = true;
        }
    }

    let
        ajaxSetupOptions = {
            timeout: 30000
        },
        documentReadyEventHandlers = [],
        supportsPassiveEvents = false;

    try
    {
        var opts = Object.defineProperty({}, "passive", {
            get: function ()
            {
                supportsPassiveEvents = true;
                return undefined;
            }
        });
        window.addEventListener("testPassive", null, opts);
        window.removeEventListener("testPassive", null, opts);
    }
    catch (e) { /* Intentionally empty */ }

    const
        animationQueues = {
            default: new WeakMap()
        },
        arrayFilter = Array.prototype.filter,
        createNextSelector = (selector, array) =>
        {
            const
                $$new = array || [];

            for (let i = 0, keys = Object.keys($$.fn); i < keys.length; i++)
                $$new[keys[i]] = $$.fn[keys[i]];

            $$new.$$previous = selector;

            return $$new;
        },
        dataMap = new WeakMap(),
        domContentLoadedHandler = () =>
        {
            document.removeEventListener("DOMContentLoaded", domContentLoadedHandler);
            documentReadyEventHandlers.forEach(item => { item(); });
            documentReadyEventHandlers = [];
        },
        getElementFromNode = node =>
        {
            if (node instanceof Element)
                return node;
            else if (node instanceof Document)
                return node.body;
        },
        getNodeFromNodeOrArrayOrSelector = nodeOrArrayOrSelector =>
        {
            if (nodeOrArrayOrSelector instanceof Node)
                return nodeOrArrayOrSelector;

            if (typeof nodeOrArrayOrSelector === "string")
                nodeOrArrayOrSelector = Sizzle(nodeOrArrayOrSelector);

            if (Array.isArray(nodeOrArrayOrSelector))
            {
                const
                    nodes = arrayFilter.call(nodeOrArrayOrSelector, item => item instanceof Node);

                if (nodes && nodes.length)
                    return nodes[0];
            }

            return undefined;
        },
        elementEventHandlerCreate = eventName =>
        {
            return function (evt)
            {
                const
                    eventTarget = evt.currentTarget ?? evt.target ?? (window.cordova ? document : undefined),
                    eventHandlers = elementEventHandlersGet(eventTarget);

                if (!eventHandlers || !eventHandlers[eventName] || !eventHandlers[eventName].length)
                    return;

                // Clone (Handlers may modify list during execution) and call those with selectors first
                const
                    eventHandlersClone = eventHandlers[eventName].orderBy(item => !item.selector),
                    event = {},
                    path = evt.path || (evt.composedPath && evt.composedPath()) || [];

                if (!path.length)
                    path.push(evt.currentTarget ?? evt.target);

                for (let property in evt)
                {
                    if (typeof evt[property] !== "function")
                        event[property] = evt[property];
                }

                event.originalEvent = evt;
                event.preventDefault = function () { this.originalEvent.preventDefault(); };
                event.stopPropagation = function () { this.originalEvent.stopPropagation(); };
                event.target = path[0];

                if (!event.currentTarget)
                    event.currentTarget = eventTarget;

                // event.currentTarget = element that the event is bound on
                // event.target = element that triggered the event

                for (let pathItem of path)
                {
                    for (let handlerInfo of eventHandlersClone)
                    {
                        if (handlerInfo.once && handlerInfo.invoked)
                            continue;

                        if (handlerInfo.selector)
                        {
                            if (!pathItem.matches)
                                continue;

                            const
                                matchingElements = Sizzle(handlerInfo.selector, evt.currentTarget ?? evt.target);

                            if (matchingElements.indexOf(pathItem) < 0)
                                continue;

                            event.currentTarget = pathItem;
                        }
                        else if (pathItem !== path[0])
                            break;
                            
                        event.data = $$.extend({}, event.data, handlerInfo.data);
                        event.namespace = handlerInfo.namespace;

                        handlerInfo.handler.apply(event.currentTarget, [event, evt.detail]);
                        handlerInfo.invoked = true;
                    }
                    
                    if (evt.cancelBubble)
                        return;
                }

                if (eventHandlers[event.type])
                {
                    for (let i = eventHandlers[event.type].length - 1; i >= 0; i--)
                    {
                        const
                            handlerInfo = eventHandlers[event.type][i];

                        if (handlerInfo.once && handlerInfo.invoked)
                            eventHandlers[event.type].splice(i, 1);
                    }
                }

            }
        },
        elementEventHandlerOff = (element, eventName, selector, data, handler) =>
        {
            const
                targetSelf = data?.self,
                eventHandlers = elementEventHandlersGet(element);

            if (!eventHandlers[eventName])
                return;

            eventHandlers[eventName].remove(
                item =>
                    (!targetSelf || item.targetSelf === targetSelf)
                    && (!selector || item.selector === selector)
                    && (!handler || item.handler === handler)
            );

            if (eventHandlers[eventName].length === 0)
            {
                const
                    eventNameIndexOfDot = eventName.indexOf(".");

                let
                    elementEventName = eventNameIndexOfDot > -1 ? eventName.substr(0, eventNameIndexOfDot) : eventName;

                if (!htmlEventNames[elementEventName])
                    elementEventName = eventName;

                element.removeEventListener(
                    elementEventName,
                    eventHandlers[eventName].elementEventHandler
                );

                delete eventHandlers[eventName];
            }
        },
        elementEventHandlerOffAll = element =>
        {
            const
                eventHandlers = elementEventHandlersGet(element);

            for (let eventName of Object.keys(eventHandlers))
            {
                const
                    eventNameIndexOfDot = eventName.indexOf(".");

                let
                    elementEventName = eventNameIndexOfDot > -1 ? eventName.substring(0, eventNameIndexOfDot) : eventName;

                if (!htmlEventNames[elementEventName])
                    elementEventName = eventName;

                element.removeEventListener(
                    elementEventName,
                    eventHandlers[eventName].elementEventHandler
                );

                delete eventHandlers[eventName];
            }
        },
        elementEventHandlerOn = (element, eventName, selector, data, handler, once) =>
        {
            if (!(element instanceof EventTarget))
                return;

            if (typeof eventName !== "string")
                return;

            if (typeof handler !== "function")
                return;

            const
                targetSelf = data?.self,
                eventHandlers = elementEventHandlersGet(element),
                alreadyRegistered =
                    eventHandlers[eventName]
                    && eventHandlers[eventName]
                        .any(item =>
                        {
                            return (
                                item.targetSelf === targetSelf
                                && item.selector === selector
                                && item.handler === handler
                            );
                        });

            if (alreadyRegistered)
                return;

            const
                handlerInfo =
                {
                    targetSelf: targetSelf,
                    selector: selector,
                    data: data,
                    handler: handler,
                    once: once
                };

            if (!eventHandlers[eventName])
                eventHandlers[eventName] = [];

            if (eventHandlers[eventName].length === 0)
            {
                eventHandlers[eventName].elementEventHandler =
                    elementEventHandlerCreate(eventName);

                const
                    eventNameIndexOfDot = eventName.indexOf(".");

                let
                    elementEventName = eventNameIndexOfDot > -1 ? eventName.substring(0, eventNameIndexOfDot) : eventName;

                if (!htmlEventNames[elementEventName])
                    elementEventName = eventName;

                const
                    capture = ["mouseenter", "mouseleave"].indexOf(elementEventName) >= 0,
                    shouldUsePassiveEvent =
                        elementEventName === "scroll"
                        || elementEventName.substring(0, 5) === "touch";

                element.addEventListener(
                    elementEventName,
                    eventHandlers[eventName].elementEventHandler,
                    shouldUsePassiveEvent && supportsPassiveEvents ? { passive: true } : capture
                );
            }

            eventHandlers[eventName].push(handlerInfo);
        },
        elementEventHandlerTrigger = (element, eventName, eventData) =>
        {
            if (eventName === "focus")
                element.focus();
            //if (element[eventName])
            //    element[eventName]();
            element.dispatchEvent(new CustomEvent(eventName, { bubbles: true, detail: eventData }));
        },
        elementEventHandlerTriggerHandler = (element, eventName, eventData) =>
        {
            if (eventName === "focus")
                element.focus();
            //if (element[eventName])
            //    element[eventName]();
            element.dispatchEvent(new CustomEvent(eventName, { bubbles: false, detail: eventData }));
        },
        elementEventHandlers = new WeakMap(),
        elementEventHandlersGet = element =>
        {
            if (!element)
                return {};

            let
                eventHandlers = elementEventHandlers.get(element);

            if (!eventHandlers)
            {
                eventHandlers = {};
                elementEventHandlers.set(element, eventHandlers);
            }

            return eventHandlers;
        },
        objectEventHandlerOff = (obj, eventName, selector, data, handler) =>
        {
            const
                targetSelf = data?.self,
                eventHandlers = objectEventHandlersGet(obj);

            if (!eventHandlers[eventName])
                return;

            eventHandlers[eventName].remove(item =>
                (!targetSelf || item.targetSelf === targetSelf)
                && (!selector || item.selector === selector)
                && (!handler || item.handler === handler)
            );

            if (eventHandlers[eventName].length === 0)
                delete eventHandlers[eventName];
        },
        objectEventHandlerOffAll = obj =>
        {
            const
                eventHandlers = objectEventHandlersGet(obj);

            for (let key of Object.keys(eventHandlers))
                delete eventHandlers[key];
        },
        objectEventHandlerOn = (obj, eventName, selector, data, handler, once) =>
        {
            const
                targetSelf = data?.self,
                eventHandlers = objectEventHandlersGet(obj),
                alreadyRegistered =
                    eventHandlers[eventName]
                    && eventHandlers[eventName]
                        .any(item =>
                        {
                            return (
                                item.targetSelf === targetSelf
                                && item.selector === selector
                                && item.handler === handler
                            );
                        });

            if (alreadyRegistered)
                return;

            const
                handlerInfo =
                {
                    targetSelf: targetSelf,
                    selector: selector,
                    data: data,
                    handler: handler,
                    once: once
                };

            if (!eventHandlers[eventName])
                eventHandlers[eventName] = [];

            eventHandlers[eventName].push(handlerInfo);
        },
        objectEventHandlerTrigger = (obj, eventName, eventData, bubble) =>
        {
            const
                nativeEvent = new Event(eventName),
                event = {};

            for (let property in nativeEvent)
            {
                if (typeof nativeEvent[property] !== "function")
                    event[property] = nativeEvent[property];
            }

            event.currentTarget = obj;
            event.originalEvent = nativeEvent;
            event.path = [];
            event.preventDefault = function () { this.originalEvent.preventDefault(); };
            event.stopPropagation = function () { this.originalEvent.stopPropagation(); };
            event.target = obj;

            let
                currentObject = obj;

            while (currentObject)
            {
                event.path.push(currentObject);

                objectEventHandlerTriggerHandlers(currentObject, event, eventData);

                if (!bubble)
                    break;

                let
                    nextObject,
                    nextElement = currentObject.$$elementGet ? currentObject.$$elementGet()?.get(0) : void 0;

                while (nextElement)
                {
                    nextElement = nextElement.parentElement;

                    if (!nextElement)
                        break;

                    if (nextElement.classList.contains("rad-control"))
                    {
                        const
                            radControl = $$.radControl(nextElement);

                        if (radControl)
                        {
                            nextObject = radControl;
                            break;
                        }
                    }
                }

                currentObject = nextObject;
            }
        },
        objectEventHandlerTriggerHandlers = (obj, event, eventData) =>
        {
            const
                eventHandlers = objectEventHandlersGet(obj);

            if (!eventHandlers || !eventHandlers[event.type] || !eventHandlers[event.type].length)
                return;

            // Clone (Handlers may modify list during execution) and call those with selectors first
            const
                eventHandlersClone = eventHandlers[event.type].orderBy(item => !item.selector);

            // event.currentTarget = element that the event is bound on
            // event.target = element that triggered the event

            for (let pathItem of event.path)
            {
                for (let handlerInfo of eventHandlersClone)
                {
                    if (handlerInfo.once && handlerInfo.invoked)
                        continue;

                    event.currentTarget = obj;

                    if (handlerInfo.selector)
                    {
                        const
                            selector =
                                handlerInfo.selector
                                    .split(",")
                                    .select(item =>
                                    {
                                        const
                                            trimmedItem = item.trim();

                                        if (trimmedItem && trimmedItem[0] === ">" && event.currentTarget.controlId)
                                            return `#${event.currentTarget.controlId} ${item}`;

                                        return item;
                                    })
                                    .join(",");

                        const
                            $$element = pathItem.$$elementGet && pathItem.$$elementGet();

                        if (!$$element || !$$element.filter(selector).length)
                            continue;

                        event.currentTarget = pathItem;
                    }
                    else if (pathItem !== event.path[0])
                        break;

                    event.data = $$.extend({}, event.data, handlerInfo.data);

                    handlerInfo.handler.apply(event.currentTarget, [event, eventData]);
                    handlerInfo.invoked = true;
                }

                if (event.originalEvent.cancelBubble)
                    return;
            }

            if (eventHandlers[event.type])
            {
                for (let i = eventHandlers[event.type].length - 1; i >= 0; i--)
                {
                    const
                        handlerInfo = eventHandlers[event.type][i];

                    if (handlerInfo.once && handlerInfo.invoked)
                        eventHandlers[event.type].splice(i, 1);
                }
            }
        },
        objectEventHandlers = new WeakMap(),
        objectEventHandlersGet = obj =>
        {
            let
                eventHandlers = objectEventHandlers.get(obj);

            if (!eventHandlers)
            {
                eventHandlers = {};
                objectEventHandlers.set(obj, eventHandlers);
            }

            return eventHandlers;
        };

    if (document.readyState === "loading")
        document.addEventListener("DOMContentLoaded", domContentLoadedHandler);

    const
        $$ = function (arrayOrElementOrFunctionOrHtmlOrSelector)
        {
            let
                arr;

            if (!arrayOrElementOrFunctionOrHtmlOrSelector)
                arr = [];
            else
            {
                if (typeof arrayOrElementOrFunctionOrHtmlOrSelector === "function")
                    return $$().ready(arrayOrElementOrFunctionOrHtmlOrSelector);
                else if (Array.isArray(arrayOrElementOrFunctionOrHtmlOrSelector))
                    arr = arrayOrElementOrFunctionOrHtmlOrSelector;
                else if (typeof arrayOrElementOrFunctionOrHtmlOrSelector === "string")
                {
                    const
                        trimmedString = arrayOrElementOrFunctionOrHtmlOrSelector.trim();

                    if (trimmedString.length >= 3 && trimmedString[0] === "<" && trimmedString[trimmedString.length - 1] === ">")
                    {
                        const tempElement = document.createElement("div");
                        tempElement.insertAdjacentHTML("beforeend", arrayOrElementOrFunctionOrHtmlOrSelector);
                        arr = Array.from(tempElement.children);
                    }
                    else
                        arr = Sizzle(arrayOrElementOrFunctionOrHtmlOrSelector);
                }
                else
                    arr = [arrayOrElementOrFunctionOrHtmlOrSelector];
            }

            for (let i = 0, keys = Object.keys($$.fn); i < keys.length; i++)
                arr[keys[i]] = $$.fn[keys[i]];

            return arr;
        },
        pow = Math.pow,
        sqrt = Math.sqrt,
        sin = Math.sin,
        cos = Math.cos,
        PI = Math.PI,
        c1 = 1.70158,
        c2 = c1 * 1.525,
        c3 = c1 + 1,
        c4 = (2 * PI) / 3,
        c5 = (2 * PI) / 4.5,
        bounceOut = x =>
        {
            const
                n1 = 7.5625,
                d1 = 2.75;

            if (x < 1 / d1)
                return n1 * x * x;
            else if (x < 2 / d1)
                return n1 * (x -= 1.5 / d1) * x + 0.75;
            else if (x < 2.5 / d1)
                return n1 * (x -= 2.25 / d1) * x + 0.9375;
            else
                return n1 * (x -= 2.625 / d1) * x + 0.984375;
        };

    $$.ajax = options =>
    {
        options = $$.extend(true, {}, {
            contentType: "application/x-www-form-urlencoded; charset=utf-8",
            headers: {},
            sendXRequestedWith: true,
            timeout: ajaxSetupOptions.timeout,
            xhrFields: {}
        }, options);

        if (options.sendXRequestedWith)
            options.headers["X-Requested-With"] = "XMLHttpRequest";

        if (options.contentType)
            options.headers["Content-Type"] = options.contentType;

        return new Promise((resolve, reject) =>
        {
            const
                xhr = new XMLHttpRequest();

            xhr.onreadystatechange = () =>
            {
                switch (xhr.readyState)
                {
                    case XMLHttpRequest.DONE:

                        $$(document).triggerHandler("ajaxComplete", { options: options, xhr: xhr });

                        if (xhr.status >= 200 && xhr.status < 300)
                        {
                            $$(document).triggerHandler("ajaxSuccess", { options: options, xhr: xhr });

                            resolve([xhr.status === 204 ? undefined : xhr.response, xhr]);
                            break;
                        }

                        $$(document).triggerHandler("ajaxError", { options: options, xhr: xhr });
                        reject(xhr);
                        break;
                }
            };

            xhr.open(options.type || "GET", options.url, true);

            if (options.dataType)
                xhr.responseType = options.dataType;

            for (let i = 0, keys = Object.keys(options.headers); i < keys.length; i++)
                xhr.setRequestHeader(keys[i], options.headers[keys[i]]);

            for (let i = 0, keys = Object.keys(options.xhrFields); i < keys.length; i++)
                xhr[keys[i]] = options.xhrFields[keys[i]];

            // Ajax Setup Options
            xhr.timeout = ajaxSetupOptions?.timeout || 0;

            if (ajaxSetupOptions?.beforeSend)
                ajaxSetupOptions.beforeSend(xhr, options);

            $$(document).triggerHandler("ajaxSend", { options: options, xhr: xhr });

            xhr.send(options.data);
        });
    };
    $$.ajaxSetup = options => { $$.extend(true, ajaxSetupOptions, options); };
    $$.contains = (element, child) => element !== child && element.contains(child);
    $$.data = (element, keyOrValue, value) =>
    {
        if (!(element instanceof Element))
            return;

        let
            key;

        if (typeof keyOrValue === "string")
            key = keyOrValue;
        else
            value = keyOrValue;

        if (typeof key === "string")
            key = key.fromDashed();

        let
            data = dataMap.get(element);

        if (!data)
        {
            data = {};
            dataMap.set(element, data);

            const
                dataTagStart = "data-",
                attributeNames = element.getAttributeNames().filter(item => item.substring(0, 5) === dataTagStart);

            if (attributeNames.length)
            {
                attributeNames.forEach(item =>
                {
                    let
                        attributeValue = element.getAttribute(item);

                    if (attributeValue && attributeValue[0] === "[" || attributeValue[0] === "{")
                    {
                        try { attributeValue = attributeValue.fromJson(); }
                        catch (e) { /* Intentionally empty */ }
                    }

                    data[item.substring(dataTagStart.length).fromDashed()] = attributeValue;
                });
            }
        }

        if (value === void 0)
            return key ? data[key] : data;

        if (key)
        {
            data[key] = value;
            return;
        }

        for (let property in value)
            data[property.fromDashed()] = value[property];
    };
    $$.each = (arrayOrObject, callback) =>
    {
        if (!arrayOrObject)
            return;

        if (typeof callback !== "function")
            return;

        if (Array.isArray(arrayOrObject))
        {
            for (let i = 0; i < arrayOrObject.length; i++)
            {
                if (callback.call(arrayOrObject[i], i, arrayOrObject[i]) === false)
                    return;
            }
            return;
        }

        for (let i = 0, keys = Object.keys(arrayOrObject); i < keys.length; i++)
        {
            if (callback.call(keys[i], i, keys[i]) === false)
                return;
        }
    };
    $$.easing = {
        linear: x => x,
        easeInQuad: x => x * x,
        easeOutQuad: x => 1 - (1 - x) * (1 - x),
        easeInOutQuad: x => x < 0.5 ? 2 * x * x : 1 - pow(-2 * x + 2, 2) / 2,
        easeInCubic: x => x * x * x,
        easeOutCubic: x => 1 - pow(1 - x, 3),
        easeInOutCubic: x => x < 0.5 ? 4 * x * x * x : 1 - pow(-2 * x + 2, 3) / 2,
        easeInQuart: x => x * x * x * x,
        easeOutQuart: x => 1 - pow(1 - x, 4),
        easeInOutQuart: x => x < 0.5 ? 8 * x * x * x * x : 1 - pow(-2 * x + 2, 4) / 2,
        easeInQuint: x => x * x * x * x * x,
        easeOutQuint: x => 1 - pow(1 - x, 5),
        easeInOutQuint: x => x < 0.5 ? 16 * x * x * x * x * x : 1 - pow(-2 * x + 2, 5) / 2,
        easeInSine: x => 1 - cos((x * PI) / 2),
        easeOutSine: x => sin((x * PI) / 2),
        easeInOutSine: x => -(cos(PI * x) - 1) / 2,
        easeInExpo: x => x === 0 ? 0 : pow(2, 10 * x - 10),
        easeOutExpo: x => x === 1 ? 1 : 1 - pow(2, -10 * x),
        easeInOutExpo: x =>
        (
            x === 0
                ? 0
                : x === 1
                    ? 1
                    : x < 0.5
                        ? pow(2, 20 * x - 10) / 2
                        : (2 - pow(2, -20 * x + 10)) / 2
        ),
        easeInCirc: x => 1 - sqrt(1 - pow(x, 2)),
        easeOutCirc: x => sqrt(1 - pow(x - 1, 2)),
        easeInOutCirc: x =>
        (
            x < 0.5
                ? (1 - sqrt(1 - pow(2 * x, 2))) / 2
                : (sqrt(1 - pow(-2 * x + 2, 2)) + 1) / 2
        ),
        easeInBack: x => c3 * x * x * x - c1 * x * x,
        easeOutBack: x => 1 + c3 * pow(x - 1, 3) + c1 * pow(x - 1, 2),
        easeInOutBack: x =>
        (
            x < 0.5
                ? (pow(2 * x, 2) * ((c2 + 1) * 2 * x - c2)) / 2
                : (pow(2 * x - 2, 2) * ((c2 + 1) * (x * 2 - 2) + c2) + 2) / 2
        ),
        easeInElastic: x =>
        (
            x === 0
                ? 0
                : x === 1
                    ? 1
                    : -pow(2, 10 * x - 10) * sin((x * 10 - 10.75) * c4)
        ),
        easeOutElastic: x =>
        (
            x === 0
                ? 0
                : x === 1
                    ? 1
                    : pow(2, -10 * x) * sin((x * 10 - 0.75) * c4) + 1
        ),
        easeInOutElastic: x =>
        (
            x === 0
                ? 0
                : x === 1
                    ? 1
                    : x < 0.5
                        ? -(pow(2, 20 * x - 10) * sin((20 * x - 11.125) * c5)) / 2
                        : (pow(2, -20 * x + 10) * sin((20 * x - 11.125) * c5)) / 2 + 1
        ),
        easeInBounce: x => 1 - bounceOut(1 - x),
        easeOutBounce: bounceOut,
        easeInOutBounce: x =>
        (
            x < 0.5
                ? (1 - bounceOut(1 - 2 * x)) / 2
                : (1 + bounceOut(2 * x - 1)) / 2
        )
    };
    $$.extend = (...args) => // [deep], target, source1, source2, .., sourceN
    {
        let
            deep = false,
            target,
            i = 0,
            j;

        if (typeof args[i] === "boolean")
        {
            deep = args[i];
            i++;
        }

        target = args[i];
        i++;

        for (; i < args.length; i++)
        {
            const
                source = args[i];

            if (source === null)
                continue;

            if (Array.isArray(source))
            {
                if (!Array.isArray(target))
                    throw new Error("Source was an array but the target was not");

                for (j = 0; j < source.length; j++)
                {
                    if (Array.isArray(source[j]))
                    {
                        if (!Array.isArray(target[j]))
                            target[j] = [];

                        $$.extend(deep, target[j], source[j]);
                    }
                    else if ($$.isPlainObject(source[j]))
                    {
                        if (!$$.isPlainObject(target[j]))
                            target[j] = {};

                        $$.extend(deep, target[j], source[j]);
                    }
                    else if (source[j] !== void 0)
                        target[j] = source[j];
                }
            }

            for (let property in source)
            {
                if (property === "__proto__")
                    continue;

                if (target === source[property])
                    continue;

                if (Array.isArray(source[property]))
                {
                    if (!Array.isArray(target[property]))
                        target[property] = [];

                    $$.extend(deep, target[property], source[property]);
                }
                else if ($$.isPlainObject(source[property]))
                {
                    if (!$$.isPlainObject(target[property]))
                        target[property] = {};

                    if (deep)
                        $$.extend(deep, target[property], source[property]);
                    else
                        target[property] = source[property];
                }
                else if (typeof source[property] === "function")
                {
                    if (Object.prototype.hasOwnProperty.call(source, property))
                        target[property] = source[property];
                }
                else if (source[property] !== void 0)
                    target[property] = source[property];
            }
        }

        return target;
    };
    $$.event = {
        special: {}
    };
    $$.expr = Sizzle.selectors;
    $$.expr.pseudos.hidden = function (elem)
    {
        return !$$.expr.pseudos.visible(elem);
    };
    $$.expr.pseudos.visible = function (elem)
    {
        return !!(elem.offsetWidth || elem.offsetHeight || elem.getClientRects().length);
    };
    $$.fn = {
        add: function (itemOrArray)
        {
            if (!itemOrArray)
                return createNextSelector(this, [...this]);

            if (Array.isArray(itemOrArray))
                return createNextSelector(this, [...this, ...itemOrArray]);

            return createNextSelector(this, [...this, itemOrArray]);
        },
        addBack: function (selector)
        {
            const
                items =
                    selector
                        ? this.$$previous.filter(selector)
                        : this.$$previous;

            if (items.length)
                return createNextSelector(this, [...this, ...items]);

            return this;
        },
        addClass: function (className)
        {
            if (className)
            {
                for (let element of this)
                {
                    if (element.classList?.add)
                    {
                        DOMTokenList.prototype.add.apply(
                            element.classList,
                            className
                                .split(" ")
                                .filter(item => !!item)
                        );
                        continue;
                    }

                    var
                        originalClassName = element.className,
                        classList =
                            originalClassName
                                ? originalClassName.split(" ").filter(item => !!item)
                                : [];

                    if (className)
                    {
                        className.split(" ")
                            .filter(item => !!item && !classList.contains(item))
                            .forEach(item => { classList.add(item); })
                    }

                    var
                        currentClassName = classList.join(" ");

                    if (currentClassName !== originalClassName)
                        element.className = currentClassName;
                }
            }

            return this;
        },
        animate: function (properties, duration, easing, complete)
        {
            const
                promises = [],
                queueName = "default";

            for (let node of this)
            {
                for (let property in properties)
                {
                    const
                        animator = new Animator(node, property, properties[property], duration, easing);

                    if (!animationQueues[queueName])
                        animationQueues[queueName] = new WeakMap();

                    let
                        animationQueue = animationQueues[queueName].get(node);

                    if (!animationQueue)
                    {
                        animationQueue = [];
                        animationQueues[queueName].set(node, animationQueue);
                    }

                    animationQueue.push(animator);

                    promises
                        .push(
                            animator
                                .start()
                                .finally(() =>
                                {
                                    const
                                        index = animationQueue.indexOf(animator);

                                    if (index > -1)
                                        animationQueue.splice(index, 1);

                                    if (complete)
                                        complete();
                                })
                        );
                }
            }

            this._promise = Promise.all(promises);

            return this;
        },
        append: function (...args)
        {
            if (!this.length)
                return this;

            const
                newItems = $$.apply($$, args),
                lastElement = this[this.length - 1];

            lastElement.append.apply(lastElement, arrayFilter.call(newItems, item => item instanceof Node));

            return this;
        },
        appendTo: function (nodeOrArrayOrSelector)
        {
            if (!this.length)
                return this;

            const
                node = getNodeFromNodeOrArrayOrSelector(nodeOrArrayOrSelector);
            
            if (node instanceof Element)
                node.append.apply(node, this);

            return this;
        },
        attr: function (...args)
        {
            switch (args.length)
            {
                case 1:
                    {
                        if (!this.length)
                            return;

                        const
                            element = getElementFromNode(this[0]);

                        if (!element)
                            return;

                        return element.getAttribute(args[0]);
                    }
                case 2:
                    {
                        const
                            remove = Object.isNullOrUndefined(args[1]);

                        for (let element of this)
                        {
                            if (remove)
                                element.removeAttribute(args[0]);
                            else
                                element.setAttribute(args[0], args[1]);
                        }
                        break;
                    }
            }

            return this;
        },
        children: function (selector)
        {
            return createNextSelector(
                this,
                arrayFilter.call(
                    this
                        .map(item => Array.from(item.children))
                        .reduce((a, b) => a.concat(b), []),
                    item => !selector || item.matches(selector)
                )
            );
        },
        clone: function (withDataAndEvents, deepWithDataAndEvents)
        {
            const
                deep = deepWithDataAndEvents === true;

            return $$(
                arrayFilter
                    .call(
                        this,
                        item => item instanceof Node
                    )
                    .map(item => item.cloneNode(deep))
            );
        },
        closest: function (selector)
        {
            return createNextSelector(
                this,
                arrayFilter.call(this.map(item => item.closest(selector)), item => !!item)
            );
        },
        css: function (...args)
        {
            let
                style;

            switch (args.length)
            {
                case 1:
                    style = args[0];
                    break;
                case 2:
                    style = {};
                    style[args[0]] = args[1];
                    break;
            }

            if (args.length == 1 && typeof style === "string")
            {
                if (!this.length || !(this[0] instanceof Element))
                    return;

                return getComputedStyle(this[0])[style];
            }

            for (let element of this)
            {
                for (let property in style)
                {
                    const
                        propertyNameDashed = property.toDashed();

                    let
                        value = style[property];

                    if (Object.isNullOrUndefined(value))
                    {
                        element.style.removeProperty(propertyNameDashed);
                        continue;
                    }

                    if (typeof value === "number" && numericToPixelProperties.indexOf(propertyNameDashed) >= 0)
                        value = `${value}px`;

                    element.style.setProperty(propertyNameDashed, value);
                }
            }

            return this;
        },
        data: function (keyOrObj, value)
        {
            const
                keyOrObjIsKey = typeof keyOrObj === "string";

            if (!keyOrObj || keyOrObjIsKey && value === void 0)
            {
                // .data()
                // .data(key)

                if (!this.length)
                    return;

                const
                    element = getElementFromNode(this[0]);

                if (!element)
                    return;

                return keyOrObjIsKey ? $$.data(element, keyOrObj) : $$.data(element);
            }

            if (keyOrObjIsKey && value !== void 0)
            {
                for (let node of this)
                {
                    const
                        element = getElementFromNode(node);

                    if (!element)
                        continue;

                    $$.data(element, keyOrObj, value);
                }

                return this;
            }

            if ($$.isPlainObject(keyOrObj))
            {
                // .data(obj)

                for (let node of this)
                {
                    const
                        element = getElementFromNode(node);

                    if (!element)
                        continue;

                    $$.data(element, keyOrObj);
                }

                return this;
            }

            return this;
        },
        detach: function ()
        {
            for (let node of this)
            {
                if (node.parentElement)
                    node.parentElement.removeChild(node);
            }

            return this;
        },
        each: function (callback)
        {
            $$.each(this, callback);
            return this;
        },
        empty: function ()
        {
            for (let node of this)
            {
                let
                    element = getElementFromNode(node);

                if (!element)
                    continue;

                element.textContent = "";
            }

            return this;
        },
        eq: function (index)
        {
            return createNextSelector(this, this.get(index));
        },
        fadeIn: function (duration, completeOrEasing, complete)
        {
            return this.fadeTo(duration, 1, completeOrEasing, complete);
        },
        fadeOut: function (duration, completeOrEasing, complete)
        {
            return this.fadeTo(duration, 0, completeOrEasing, complete);
        },
        fadeTo: function (duration, opacity, completeOrEasing, complete)
        {
            let
                easing;

            if (typeof completeOrEasing === "string")
                easing = completeOrEasing;
            else if (typeof completeOrEasing === "function")
                complete = completeOrEasing;

            return this.animate({ opacity: opacity }, duration, easing, complete);
        },
        filter: function (selector)
        {
            return createNextSelector(
                this,
                arrayFilter.call(this, item => item.matches(selector))
            );
        },
        find: function (selector)
        {
            return createNextSelector(
                this,
                this
                    .map(item => Sizzle(selector, item))
                    .reduce((a, b) => a.concat(b), [])
            );
        },
        finish: function ()
        {
            const
                queueName = "default";

            if (!animationQueues[queueName])
                return;

            for (let node of this)
            {
                const
                    animationQueue = animationQueues[queueName].get(node);

                if (animationQueue && animationQueue.length)
                    animationQueue.forEach(item => { item.finish(); });

                animationQueues[queueName].delete(node);
            }

            return this;
        },
        first: function ()
        {
            return this.length ? createNextSelector(this, [this[0]]) : createNextSelector(this);
        },
        get: function (index)
        {
            if (index < 0)
                index = this.length + index;

            if (index >= 0 && index < this.length)
                return this[index];
        },
        hasClass: function (className)
        {
            if (!className || !this.length)
                return false;

            const
                classNames = className.split(' ').filter(item => !!item);

            if (!classNames.length)
                return false;

            for (let element of this)
            {
                if (!element.classList)
                    continue;

                let
                    fullMatch = true;

                for (let item of classNames)
                {
                    if (!element.classList.contains(item))
                    {
                        fullMatch = false;
                        break;
                    }
                }

                if (fullMatch)
                    return true;
            }

            return false;
        },
        height: function (...args)
        {
            if (!args.length)
            {
                if (!this.length)
                    return;

                const
                    element = getElementFromNode(this[0]);

                if (!element)
                    return;

                return parseFloat(getComputedStyle(element).height);
            }

            let
                value = args[0];

            if (typeof value === "function")
                value = value();

            for (let node of this)
            {
                const
                    element = getElementFromNode(node);

                if (!element)
                    continue;

                element.style.height = typeof value === "string" ? value : `${value}px`;
            }

            return this;
        },
        hide: function ()
        {
            for (let element of this)
                element.style.display = "none";

            return this;
        },
        html: function (...args)
        {
            switch (args.length)
            {
                case 0:
                    return (
                        this
                            .map(item => item.innerHTML)
                            .reduce((a, b) => a.concat(b), "")
                    );
                case 1:
                    for (let element of this)
                        element.innerHTML = args[0];
            }

            return this;
        },
        index: function (itemOrArray)
        {
            if (!itemOrArray)
            {
                if (this.length && this[0].parentElement)
                    return Array.prototype.indexOf.call(this[0].parentElement.children, this[0]);
            }

            let
                item = itemOrArray;

            if (Array.isArray(item))
            {
                if (item.length)
                    item = item[0];
            }

            return item ? this.indexOf(item) : -1;
        },
        innerHeight: function (...args)
        {
            if (!args.length)
            {
                if (!this.length)
                    return;

                const
                    element = getElementFromNode(this[0]);

                if (!element)
                    return;

                return element.clientHeight;
            }

            throw new Error("Not implemented yet");
        },
        innerWidth: function (...args)
        {
            if (!args.length)
            {
                if (!this.length)
                    return;

                const
                    element = getElementFromNode(this[0]);

                if (!element)
                    return;

                return element.clientWidth;
            }

            throw new Error("Not implemented yet");
        },
        insertAfter: function (nodeOrArrayOrSelector)
        {
            if (!this.length)
                return this;

            const
                targetNode = getNodeFromNodeOrArrayOrSelector(nodeOrArrayOrSelector);

            if (targetNode && targetNode.parentElement)
            {
                for (let node of this)
                {
                    if (targetNode.nextSibling)
                        targetNode.parentElement.insertBefore(node, targetNode.nextSibling);
                    else
                        targetNode.parentElement.append(node);
                }
            }

            return this;
        },
        insertBefore: function (nodeOrArrayOrSelector)
        {
            if (!this.length)
                return this;

            const
                targetNode = getNodeFromNodeOrArrayOrSelector(nodeOrArrayOrSelector);

            if (targetNode && targetNode.parentElement)
            {
                for (let node of this)
                    targetNode.parentElement.insertBefore(node, targetNode);
            }

            return this;
        },
        is: function (itemOrArrayOrSelector)
        {
            if (typeof itemOrArrayOrSelector === "string")
                return !!Sizzle.matches(itemOrArrayOrSelector, this).length;

            if (Array.isArray(itemOrArrayOrSelector))
                return this.length === itemOrArrayOrSelector.length && this.all(item => itemOrArrayOrSelector.indexOf(item) >= 0);

            return this.length === 1 && this[0] === itemOrArrayOrSelector;
        },
        last: function ()
        {
            return this.length ? createNextSelector(this, [this[this.length - 1]]) : createNextSelector(this);
        },
        next: function (selector)
        {
            return createNextSelector(
                this,
                arrayFilter.call(
                    this
                        .map(item =>
                        {
                            let
                                elementSibling = item.nextElementSibling;

                            if (!selector)
                                return elementSibling;

                            if (typeof selector === "string" && elementSibling.matches(selector))
                                return elementSibling;

                            if (Array.isArray(selector) && selector.indexOf(elementSibling) > -1)
                                return elementSibling;
                        }),
                    (item, index, arr) => !!item && arr.indexOf(item) === index
                )
            );
        },
        nextAll: function (selector)
        {
            return createNextSelector(
                this,
                arrayFilter.call(
                    this
                        .map(item =>
                        {
                            let
                                elementSibling = item.nextElementSibling;

                            if (!selector)
                                return elementSibling;

                            while (elementSibling)
                            {
                                if (typeof selector === "string" && elementSibling.matches(selector))
                                    return elementSibling;

                                if (Array.isArray(selector) && selector.indexOf(elementSibling) > -1)
                                    return elementSibling;

                                elementSibling = elementSibling.nextElementSibling
                            }

                            return;
                        }),
                    (item, index, arr) => !!item && arr.indexOf(item) === index
                )
            );
        },
        not: function (nodeOrArrayOrSelector)
        {
            if (typeof nodeOrArrayOrSelector === "string")
                nodeOrArrayOrSelector = this.filter(nodeOrArrayOrSelector);

            if (nodeOrArrayOrSelector instanceof Node)
                return createNextSelector(this, arrayFilter.call(this, item => item !== nodeOrArrayOrSelector));
            else if (Array.isArray(nodeOrArrayOrSelector))
            {
                const
                    elements = arrayFilter.call(nodeOrArrayOrSelector, item => item instanceof Node);

                return createNextSelector(this, arrayFilter.call(this, item => elements.indexOf(item) < 0));
            }

            return this;
        },
        off: function (...args)
        {
            var
                events,
                selector,
                data,
                handler;

            switch (args.length)
            {
                case 0: // Remove all events
                    for (let node of this)
                    {
                        const
                            element = getElementFromNode(node);

                        if (element)
                        {
                            elementEventHandlerOffAll(element);
                            continue;
                        }

                        objectEventHandlerOffAll(node);
                    }

                    return this;
                case 1: // events
                    events = args[0];
                    break;
                case 2: // events, handler
                    events = args[0];
                    handler = args[1];
                    break;
                case 3:
                    if (typeof args[1] === "string")
                    {
                        // events, selector, handler
                        events = args[0];
                        selector = args[1];
                        handler = args[2];
                    }
                    else
                    {
                        // events, data, handler
                        events = args[0];
                        data = args[1];
                        handler = args[2];
                    }
                    break;
                case 4: // events, selector, data, handler
                    events = args[0];
                    selector = args[1];
                    data = args[2];
                    handler = args[3];
                    break;
            }

            const
                eventNames = events.split(" ").filter(item => !!item);

            for (let eventName of eventNames)
            {
                const
                    specialEvent = $$.event.special[eventName];

                for (let node of this)
                {
                    if (node instanceof EventTarget)
                        elementEventHandlerOff(node, eventName, selector, data, handler);
                    else
                        objectEventHandlerOff(node, eventName, selector, data, handler);

                    if (specialEvent)
                    {
                        if (specialEvent.teardown)
                            specialEvent.teardown.call(node);

                        continue;
                    }
                }
            }

            return this;
        },
        offset: function ()
        {
            if (!this.length || !this[0].getBoundingClientRect)
                return;

            const
                rect = this[0].getBoundingClientRect();

            //return {
            //    top: rect.top + window.pageYOffset - document.documentElement.clientTop,
            //    left: rect.left + window.pageXOffset - document.documentElement.clientLeft
            //};

            return {
                top: rect.top + document.body.scrollTop,
                left: rect.left + document.body.scrollLeft
            };
        },
        offsetParent: function ()
        {
            if (!this.length)
                return;

            return createNextSelector(
                this,
                [this[0].offsetParent || this[0]]
            );
        },
        on: function (...args)
        {
            var
                events,
                selector,
                data,
                handler;

            switch (args.length)
            {
                case 0:
                    return this;
                case 1: // events
                    events = args[0];
                    break;
                case 2: // events, handler
                    events = args[0];
                    handler = args[1];
                    break;
                case 3:
                    if (typeof args[1] === "string")
                    {
                        // events, selector, handler
                        events = args[0];
                        selector = args[1];
                        handler = args[2];
                    }
                    else
                    {
                        // events, data, handler
                        events = args[0];
                        data = args[1];
                        handler = args[2];
                    }
                    break;
                case 4: // events, selector, data, handler
                    events = args[0];
                    selector = args[1];
                    data = args[2];
                    handler = args[3];
                    break;
            }

            const
                eventNames = events.split(" ").filter(item => !!item);

            for (let eventName of eventNames)
            {
                const
                    specialEvent = $$.event.special[eventName];

                for (let node of this)
                {
                    if (node instanceof EventTarget)
                        elementEventHandlerOn(node, eventName, selector, data, handler);
                    else
                        objectEventHandlerOn(node, eventName, selector, data, handler);

                    if (specialEvent)
                    {
                        if (specialEvent.setup)
                            specialEvent.setup.call(node);

                        continue;
                    }
                }
            }

            return this;
        },
        one: function (...args)
        {
            var
                events,
                selector,
                data,
                handler;

            switch (args.length)
            {
                case 0:
                    return this;
                case 1: // events
                    events = args[0];
                    break;
                case 2: // events, handler
                    events = args[0];
                    handler = args[1];
                    break;
                case 3:
                    if (typeof args[1] === "string")
                    {
                        // events, selector, handler
                        events = args[0];
                        selector = args[1];
                        handler = args[2];
                    }
                    else
                    {
                        // events, data, handler
                        events = args[0];
                        data = args[1];
                        handler = args[2];
                    }
                    break;
                case 4: // events, selector, data, handler
                    events = args[0];
                    selector = args[1];
                    data = args[2];
                    handler = args[3];
                    break;
            }

            const
                eventNames = events.split(" ").filter(item => !!item);

            for (let eventName of eventNames)
            {
                const
                    specialEvent = $$.event.special[eventName];

                for (let node of this)
                {
                    if (node instanceof EventTarget)
                        elementEventHandlerOn(node, eventName, selector, data, handler, true);
                    else
                        objectEventHandlerOn(node, eventName, selector, data, handler, true);

                    if (specialEvent)
                    {
                        if (specialEvent.setup)
                            specialEvent.setup.call(node);

                        continue;
                    }
                }
            }

            return this;
        },
        outerHeight: function (...args)
        {
            let
                includeMargin,
                value;

            if (args.length)
            {
                if (typeof args[0] === "boolean")
                    includeMargin = args[0];
                else
                {
                    value = args[0];

                    if (args.length > 1 && typeof args[1] === "boolean")
                        includeMargin = args[1];
                }
            }

            if (!value)
            {
                if (!this.length)
                    return;

                if (!includeMargin)
                    return this[0].offsetHeight;

                const
                    style = getComputedStyle(this[0]);

                return this[0].offsetHeight + parseFloat(style.marginTop) + parseFloat(style.marginBottom);
            }

            throw new Error("Not implemented yet");
        },
        outerWidth: function (...args)
        {
            let
                includeMargin,
                value;

            if (args.length)
            {
                if (typeof args[0] === "boolean")
                    includeMargin = args[0];
                else
                {
                    value = args[0];

                    if (args.length > 1 && typeof args[1] === "boolean")
                        includeMargin = args[1];
                }
            }

            if (!value)
            {
                if (!this.length)
                    return;

                if (!includeMargin)
                    return this[0].offsetWidth;

                const
                    style = getComputedStyle(this[0]);

                return this[0].offsetWidth + parseFloat(style.marginLeft) + parseFloat(style.marginRight);
            }

            throw new Error("Not implemented yet");
        },
        parent: function ()
        {
            return createNextSelector(
                this,
                arrayFilter.call(this.map(item => item.parentElement), item => !!item)
            );
        },
        parents: function (selector)
        {
            return createNextSelector(
                this,
                arrayFilter.call(
                    this
                        .map(item =>
                        {
                            const
                                parents = [];

                            let
                                parentElement = item.parentElement;

                            while (parentElement)
                            {
                                if (!selector || parentElement.matches(selector))
                                    parents.push(parentElement);

                                parentElement = parentElement.parentElement;
                            }

                            return parents;
                        })
                        .reduce((a, b) => a.concat(b), []),
                    (item, index, arr) => arr.indexOf(item) === index
                )
            );
        },
        position: function ()
        {
            if (!this.length)
                return;

            const
                offset = this.offset(),
                parentOffset = this.offsetParent().offset();

            return {
                left: offset.left - parentOffset.left,
                top: offset.top - parentOffset.top
            };
        },
        prepend: function (nodeOrArrayOrHtml)
        {
            if (typeof nodeOrArrayOrHtml === "string")
                nodeOrArrayOrHtml = $$(nodeOrArrayOrHtml);

            if (this.length)
            {
                const
                    lastElement = this[this.length - 1];

                if (nodeOrArrayOrHtml instanceof Node)
                    lastElement.prepend(nodeOrArrayOrHtml);
                else if (Array.isArray(nodeOrArrayOrHtml))
                    lastElement.prepend.apply(lastElement, arrayFilter.call(nodeOrArrayOrHtml, item => item instanceof Node));
            }
            return this;
        },
        prependTo: function (element)
        {
            element.prepend.apply(element, this);
            return this;
        },
        prev: function (selector)
        {
            return createNextSelector(
                this,
                arrayFilter.call(
                    this
                        .map(item =>
                        {
                            let
                                elementSibling = item.previousElementSibling;

                            if (!selector)
                                return elementSibling;

                            if (typeof selector === "string" && elementSibling.matches(selector))
                                return elementSibling;

                            if (Array.isArray(selector) && selector.indexOf(elementSibling) > -1)
                                return elementSibling;
                        }),
                    (item, index, arr) => !!item && arr.indexOf(item) === index
                )
            );
        },
        prevAll: function (selector)
        {
            return createNextSelector(
                this,
                arrayFilter.call(
                    this
                        .map(item =>
                        {
                            let
                                elementSibling = item.previousElementSibling;

                            if (!selector)
                                return elementSibling;

                            while (elementSibling)
                            {
                                if (typeof selector === "string" && elementSibling.matches(selector))
                                    return elementSibling;

                                if (Array.isArray(selector) && selector.indexOf(elementSibling) > -1)
                                    return elementSibling;

                                elementSibling = elementSibling.previousElementSibling
                            }

                            return;
                        }),
                    (item, index, arr) => !!item && arr.indexOf(item) === index
                )
            );
        },
        promise: function ()
        {
            return this._promise || Promise.resolve();
        },
        prop: function (...args)
        {
            switch (args.length)
            {
                case 1:
                    return this.length ? this[0][args[0]] : null;
                case 2:
                    for (let element of this)
                        element[args[0]] = args[1];
                    break;
            }

            return this;
        },
        ready: function (fn)
        {
            if (typeof fn === "function")
            {
                if (document.readyState === "loading")
                    documentReadyEventHandlers.push(fn);
                else
                    fn();
            }

            return this;
        },
        remove: function ()
        {
            for (let node of this)
            {
                const
                    element = getElementFromNode(node);

                if (!element)
                    continue;

                $$(element)
                    .add(element.querySelectorAll("*"))
                    .off()
                    .removeData()
                    .stop(true, true);

                if (element.parentElement)
                    element.parentElement.removeChild(element);
            }

            return this;
        },
        removeAttr: function (attrName)
        {
            for (let element of this)
                element.removeAttribute(attrName);

            return this;
        },
        removeData: function (...args)
        {
            for (let node of this)
            {
                const
                    element = getElementFromNode(node);

                if (!element)
                    continue;

                if (!args.length)
                    dataMap.delete(element);
                else
                {
                    const
                        data = dataMap.get(element);

                    if (data)
                        delete data[args[0]];
                }
            }

            return this;
        },
        removeClass: function (className)
        {
            for (let element of this)
            {
                if (!className)
                {
                    if (element.className)
                        element.className = "";

                    continue;
                }

                if (element.classList?.remove)
                {
                    DOMTokenList.prototype.remove.apply(
                        element.classList,
                        className
                            .split(" ")
                            .filter(item => !!item)
                    );
                    continue;
                }

                var
                    originalClassName = element.className,
                    classList =
                        originalClassName
                            ? originalClassName.split(" ").filter(item => !!item)
                            : [];

                if (className)
                {
                    className
                        .split(" ")
                        .filter(item => !!item)
                        .forEach(item => { classList.remove(item); })
                }

                var
                    currentClassName = classList.join(" ");

                if (currentClassName !== originalClassName)
                    element.className = currentClassName;
            }

            return this;
        },
        scrollLeft: function (...args)
        {
            if (!args.length)
            {
                if (!this.length)
                    return;

                const
                    element = getElementFromNode(this[0]);

                if (!element)
                    return;

                return element.scrollLeft;
            }

            for (let node of this)
            {
                const
                    element = getElementFromNode(node);

                if (!element)
                    continue;

                element.scrollLeft = args[0] || 0;
            }

            return this;
        },
        scrollTop: function (...args)
        {
            if (!args.length)
            {
                if (!this.length)
                    return;

                const
                    element = getElementFromNode(this[0]);

                if (!element)
                    return;

                return element.scrollTop;
            }

            for (let node of this)
            {
                const
                    element = getElementFromNode(node);

                if (!element)
                    continue;

                element.scrollTop = args[0] || 0;
            }

            return this;
        },
        show: function ()
        {
            for (let element of this)
                element.style.display = "";

            return this;
        },
        slideDown: function (duration, completeOrEasing, complete)
        {
            let
                easing;

            if (typeof completeOrEasing === "string")
                easing = completeOrEasing;
            else if (typeof completeOrEasing === "function")
                complete = completeOrEasing;

            return this.animate({ height: 200 }, duration, easing, complete);
        },
        slideUp: function (duration, completeOrEasing, complete)
        {
            let
                easing;

            if (typeof completeOrEasing === "string")
                easing = completeOrEasing;
            else if (typeof completeOrEasing === "function")
                complete = completeOrEasing;

            return this.animate({ height: 0 }, duration, easing, complete);
        },
        stop: function (...args)
        {
            let
                clearQueue = false,
                jumpToEnd = false,
                queueName = "default";

            if (args.length)
            {
                if (typeof args[0] === "boolean")
                {
                    switch (args.length)
                    {
                        case 1:
                            clearQueue = args[0];
                            break;
                        case 2:
                            clearQueue = args[0];
                            jumpToEnd = args[1];
                            break;
                    }
                }
                else if (typeof args[0] === "string")
                {
                    switch (args.length)
                    {
                        case 1:
                            queueName = args[0];
                            break;
                        case 2:
                            queueName = args[0];
                            clearQueue = args[1];
                            break;
                        case 3:
                            queueName = args[0];
                            clearQueue = args[1];
                            jumpToEnd = args[2];
                            break;
                    }
                }
            }

            if (!animationQueues[queueName])
                return;

            for (let node of this)
            {
                const
                    animationQueue = animationQueues[queueName].get(node);

                if (animationQueue && animationQueue.length)
                {
                    animationQueue.forEach(item =>
                    {
                        if (jumpToEnd)
                            item.finish();
                        else
                            item.stop();
                    });
                }

                if (clearQueue)
                    animationQueues[queueName].delete(node);
            }

            return this;
        },
        text: function (...args)
        {
            if (!args.length)
            {
                return (
                    this
                        .map(item => item.textContent)
                        .reduce((a, b) => a.concat(b), "")
                );
            }

            for (let element of this)
                element.textContent = args[0] ?? "";

            return this;
        },
        toArray: function ()
        {
            return Array.from(this);
        },
        trigger: function (eventName, eventData)
        {
            for (let node of this)
            {
                if (node instanceof EventTarget)
                {
                    elementEventHandlerTrigger(node, eventName, eventData);
                    continue;
                }

                objectEventHandlerTrigger(node, eventName, eventData, true);
            }

            return this;
        },
        triggerHandler: function (eventName, eventData)
        {
            for (let node of this)
            {
                if (node instanceof EventTarget)
                {
                    elementEventHandlerTriggerHandler(node, eventName, eventData);
                    continue;
                }

                objectEventHandlerTrigger(node, eventName, eventData, false);
            }

            return this;
        },
        unwrap: function (selector)
        {
            const
                parentElements = new Set();

            for (let node of this)
            {
                const
                    element = getElementFromNode(node);

                if (!element)
                    continue;

                const
                    parentElement = element.parentElement;

                if (!parentElement)
                    continue;

                if (selector && !parentElement.matches(selector))
                    continue;

                parentElements.add(element.parentElement);

                const
                    grandParentElement = parentElement.parentElement;

                if (!grandParentElement)
                    continue;

                grandParentElement.insertBefore(element, parentElement);
            }

            for (let element of parentElements)
                element.remove();

            return this;
        },
        val: function (value)
        {
            if (value === void 0)
            {
                if (!this.length || !(this[0] instanceof Element))
                    return;

                return this[0].value;
            }

            if (value === null)
                value = "";

            if (typeof value !== "string")
                value = value.toString();

            for (let node of this)
                node.value = value;

            return this;
        },
        width: function (...args)
        {
            if (!args.length)
            {
                if (!this.length)
                    return;

                const
                    element = getElementFromNode(this[0]);

                if (!element)
                    return;

                return parseFloat(getComputedStyle(element).width);
            }

            let
                value = args[0];

            if (typeof value === "function")
                value = value();

            for (let node of this)
            {
                const
                    element = getElementFromNode(node);

                if (!element)
                    continue;

                element.style.width = typeof value === "string" ? value : `${value}px`;
            }

            return this;
        },
        wrap: function (arrayOrElementOrFunctionOrHtmlOrSelector)
        {
            if (this.length && arrayOrElementOrFunctionOrHtmlOrSelector)
            {
                for (let node of this)
                {
                    const
                        element = getElementFromNode(node);

                    if (!element)
                        continue;

                    let
                        wrapSelection = arrayOrElementOrFunctionOrHtmlOrSelector;

                    if (typeof wrapSelection === "function")
                        wrapSelection = wrapSelection.call(element);

                    if (typeof wrapSelection === "string")
                        wrapSelection = $$(wrapSelection);

                    let
                        wrapElement;

                    if (wrapSelection instanceof Element)
                        wrapElement = wrapSelection;
                    else if (Array.isArray(wrapSelection) && wrapSelection.length && wrapSelection[0] instanceof Element)
                        wrapElement = wrapSelection[0];

                    if (wrapElement)
                    {
                        if (element.parentElement)
                            element.parentElement.insertBefore(wrapElement, element);

                        while (wrapElement.firstChild)
                            wrapElement = wrapElement.firstChild;

                        wrapElement.append(element);
                    }
                }
            }

            return this;
        },
        wrapAll: function (arrayOrElementOrFunctionOrHtmlOrSelector)
        {
            const
                elements =
                    arrayFilter.call(
                        this
                            .map(item => getElementFromNode(item)),
                        item => !!item
                    );

            if (elements.length && arrayOrElementOrFunctionOrHtmlOrSelector)
            {
                let
                    wrapSelection = arrayOrElementOrFunctionOrHtmlOrSelector;

                if (typeof wrapSelection === "function")
                    wrapSelection = wrapSelection.call(elements);

                if (typeof wrapSelection === "string")
                    wrapSelection = $$(wrapSelection);

                let
                    wrapElement;

                if (wrapSelection instanceof Element)
                    wrapElement = wrapSelection;
                else if (Array.isArray(wrapSelection) && wrapSelection.length && wrapSelection[0] instanceof Element)
                    wrapElement = wrapSelection[0];

                if (wrapElement)
                {
                    if (elements[0].parentElement)
                        elements[0].parentElement.insertBefore(wrapElement, elements[0]);

                    while (wrapElement.firstChild)
                        wrapElement = wrapElement.firstChild;

                    for (let element of elements)
                        wrapElement.append(element);
                }
            }

            return this;
        }
    };

    const
        eventShortcutFnNames = {
            browser: [
                "error",
                "resize",
                "scroll"
            ],
            form: [
                "blur",
                "change",
                "focus",
                "focusin",
                "focusout",
                "select",
                "submit",
            ],
            keyboard: [
                "keydown",
                "keypress",
                "keyup",
            ],
            mouse: [
                "click",
                "contextmenu",
                "dblclick",
                "hover",
                "mousedown",
                "mouseenter",
                "mouseleave",
                "mousemove",
                "mouseout",
                "mouseover",
                "mouseup",
            ]
        };

    for (let i = 0, keys = Object.keys(eventShortcutFnNames); i < keys.length; i++)
    {
        for (let j = 0; j < eventShortcutFnNames[keys[i]].length; j++)
        {
            const
                eventShortcutName = eventShortcutFnNames[keys[i]][j];

            if ($$.fn[eventShortcutName])
                continue;

            $$.fn[eventShortcutName] = function (name)
            {
                return function (eventDataOrHandler, handler)
                {
                    let
                        eventData;

                    if (typeof eventDataOrHandler === "function")
                        handler = eventDataOrHandler;
                    else
                        eventData = eventDataOrHandler;

                    if (eventData === void 0 && handler === void 0)
                        return this.trigger(name);

                    return this.on(name, eventData, handler);
                };
            }(eventShortcutName);
        }
    }
    
    $$.isPlainObject = obj =>
    {
        return (
            obj !== null
            && typeof obj === "object"
            && obj.constructor === Object
            && Object.prototype.toString.call(obj) === "[object Object]"
        );
    };
    window.$$ = $$;

})();
